import React from 'react';
import PropTypes from 'prop-types';
import {withStyles} from '@material-ui/core/styles';
import DashboardTemplate from "../../Common/DashboardTemplate";
import {Checkbox, CircularProgress, FormControl, InputLabel, MenuItem, Button, Select, InputAdornment} from "@material-ui/core";
import axios from 'axios';
import {ServiceLocator} from "../../../Services/ServiceLocator";
import TextField from "@material-ui/core/TextField";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTrash, faCheck, faGripVertical} from "@fortawesome/free-solid-svg-icons";
import { v4 as uuid } from 'uuid';
import Autocomplete from "@material-ui/lab/Autocomplete";
import DrawerMenuRight from "../../Common/DrawerMenuRight";
import PrintIcon from '@material-ui/icons/Print';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { AuthenticationService } from "../../../Services/AuthenticationService";
import { parseNumberInputValue } from "../../../utils/numberInputUtils";
import PartAutocompleteOption from '../../Common/PartAutocompleteOption';
import PartDisplay from '../../Common/PartDisplay';

let parts = [];
let assemblies = [];
let subAssemblies = [];
let partFamilies = [];
let assemblyFamilies = [];
let globalProps;

const styles = theme => ({
    wrapper: {
        margin: -5,
    },
    toolbar: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        marginBottom: 20,
        gap: '24px'
    },
    toolbarLeft: {
        display: 'flex',
        alignItems: 'center',
    },
    toolbarRight: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
    },
});

// Helper functions for determining currently selling assembly
const getCurrentlySellingAssemblyId = (assemblyId, assemblies) => {
    // Get all assemblies with the same assemblyId that don't have a finalOrderNumber
    const assembliesWithoutFinalOrder = assemblies.filter(assembly => 
        assembly.assembly.assemblyId === assemblyId && 
        !assembly.assembly.finalOrderNumber
    );
    
    // If only one assembly without finalOrderNumber, it's the currently selling one
    if (assembliesWithoutFinalOrder.length === 1) {
        return assembliesWithoutFinalOrder[0].assembly.id;
    }
    
    // If multiple assemblies without finalOrderNumber, find the one with the lowest revision
    if (assembliesWithoutFinalOrder.length > 1) {
        // Sort by revision (ascending) and return the lowest
        const sortedAssemblies = assembliesWithoutFinalOrder.sort((a, b) => {
            const revisionA = a.assembly.revision || '';
            const revisionB = b.assembly.revision || '';
            return revisionA.localeCompare(revisionB, undefined, { numeric: true, sensitivity: 'base' });
        });
        return sortedAssemblies[0].assembly.id;
    }
    
    // If no assemblies without finalOrderNumber, return null
    return null;
};

const isAssemblyCurrentlySelling = (assembly, assemblies) => {
    if (!assembly || assembly.finalOrderNumber) {
        return false;
    }
    
    const currentlySellingId = getCurrentlySellingAssemblyId(assembly.assemblyId, assemblies);
    return currentlySellingId === assembly.id;
};

class AdminFamilies extends React.Component {
    state = {
        loading: true,
        user: {},
        permissions: [],
        selectedType: 'Parts', // 'Parts' or 'Assemblies' - default to Parts
        
        // Part Family States
        partFamily: null,
        selectedFamily: null,
        newPartFamilyName: '',
        newPartFamilyDescription: '',
        newPartFamilyNickname: '',
        newPartFamilyImageURL: '',
        editPartFamilyOpen: false,
        editingPartFamily: null,
        editingPartFamilyOriginal: null,
        partToFamilyOpen: false,
        selectedPartForFamily: null,
        newPartToFamilyPackageQuantity: '',
        newPartToFamilyPurchaseQuantity: '',
        newPartToFamilyIsImpliedPart: false,
        partFamilyRelationships: [],
        editingRelationship: null,
        editingField: null,
        hasQuantityChanges: false,
        modifiedRelationships: {},
        
        // Part Family Bulk Edit States
        pendingParts: [], // Array of parts to be added
        showPartBulkEdit: false,
        
        // Assembly Family States
        assemblyFamily: null,
        editingAssemblyFamily: null,
        newFamilyName: '',
        newFamilyDescription: '',
        editAssemblyFamilyOpen: false,
        editingAssemblyFamilyOriginal: null,
        assemblyToFamilyOpen: false,
        selectedAssemblyForFamily: '',
        
        // Assembly Family Bulk Edit States
        pendingAssemblies: [], // Array of assemblies to be added
        showAssemblyBulkEdit: false,
        
        // New Family Creation States
        newPartFamilyOpen: false,
        newAssemblyFamilyOpen: false,
        
        savingNewValue: false,
    };

    constructor(props) {
        super(props);
        globalProps = props;

        const jwtToken = AuthenticationService.getInstance().getToken();
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + jwtToken;
    }

    normalizeFileURLsForPayload = (fileURLs) => {
        if (Array.isArray(fileURLs)) {
            const filtered = fileURLs.filter(x => x && x.trim() !== '');
            return filtered.length > 0 ? filtered.join('~|~') : '';
        }
        if (typeof fileURLs === 'string') {
            return fileURLs;
        }
        return '';
    };

    componentDidMount() {
        this.loadStoredState();
        this.loadData();
    }

    loadStoredState = () => {
        const storedState = sessionStorage.getItem('familiesState');
        if (storedState) {
            try {
                const parsed = JSON.parse(storedState);
                this.setState({
                    selectedType: parsed.selectedType || 'Parts',
                    partFamily: parsed.partFamily || null,
                    assemblyFamily: parsed.assemblyFamily || null
                });
            } catch (e) {
                console.warn('Failed to parse stored families state:', e);
            }
        }
    };

    saveStateToStorage = () => {
        const stateToSave = {
            selectedType: this.state.selectedType,
            partFamily: this.state.partFamily,
            assemblyFamily: this.state.assemblyFamily
        };
        sessionStorage.setItem('familiesState', JSON.stringify(stateToSave));
    };

    loadData = () => {
        const self = this;
        const user = AuthenticationService.getInstance().getUser();
        if (!user || !user.userPermissions || !user.userPermissions.includes('Workshop')) {
            globalProps.history.push('/login');
            return;
        }

        self.setState({loading: true});
        Promise.all([
            self.loadParts(), 
            self.loadAssemblies(), 
            self.loadSubAssemblies(), 
            self.loadPartFamilies(), 
            self.loadAssemblyFamilies()
        ]).then(() => {
            // Restore editingAssemblyFamily if assemblyFamily is set
            let editingAssemblyFamily = null;
            if (self.state.assemblyFamily) {
                editingAssemblyFamily = assemblyFamilies.find(x => x.id === self.state.assemblyFamily);
            }
            
            self.setState({
                loading: false, 
                user, 
                parts, 
                assemblies, 
                subAssemblies, 
                assembliesAndSubAssemblies: assemblies.concat(subAssemblies), 
                assemblyFamilies,
                partFamilies,
                permissions: user.userPermissions,
                editingAssemblyFamily
            });
        });
    };

    loadParts = () => {
        const partService = ServiceLocator.Instance.getPartService();
        return partService.getAllPartsExpanded().then((partsResult) => {
            parts = partsResult.sort((a, b) => {
                if (a.part.partType !== b.part.partType) {
                    if (a.part.partType < b.part.partType) return -1;
                    if (a.part.partType > b.part.partType) return 1;
                }
                if (a.part.manufacturerFor !== b.part.manufacturerFor) {
                    if (a.part.manufacturerFor < b.part.manufacturerFor) return -1;
                    if (a.part.manufacturerFor > b.part.manufacturerFor) return 1;
                }
                if (a.part.partId !== b.part.partId) {
                    if (a.part.partId < b.part.partId) return -1;
                    if (a.part.partId > b.part.partId) return 1;
                }
                return 0;
            });
            this.setState({parts});
        });
    };

    loadAssemblies = () => {
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        return assemblyService.getAllAssembliesExpanded().then((assembliesResult) => {
            assemblies = assembliesResult.sort((a, b) => {
                // First, sort by assemblyFamilyOrder if both assemblies belong to the same family
                if (a.assembly.assemblyFamilyId && b.assembly.assemblyFamilyId && 
                    a.assembly.assemblyFamilyId === b.assembly.assemblyFamilyId) {
                    const orderA = a.assembly.assemblyFamilyOrder || 0;
                    const orderB = b.assembly.assemblyFamilyOrder || 0;
                    if (orderA !== orderB) {
                        return orderA - orderB;
                    }
                }
                
                // Then sort by the original criteria
                if (a.assembly.assemblyType !== b.assembly.assemblyType) {
                    if (a.assembly.assemblyType < b.assembly.assemblyType) return -1;
                    if (a.assembly.assemblyType > b.assembly.assemblyType) return 1;
                }
                if (a.assembly.manufacturerFor !== b.assembly.manufacturerFor) {
                    if (a.assembly.manufacturerFor < b.assembly.manufacturerFor) return -1;
                    if (a.assembly.manufacturerFor > b.assembly.manufacturerFor) return 1;
                }
                if (a.assembly.assemblyId !== b.assembly.assemblyId) {
                    if (a.assembly.assemblyId < b.assembly.assemblyId) return -1;
                    if (a.assembly.assemblyId > b.assembly.assemblyId) return 1;
                }
                return 0;
            });
            this.setState({assemblies});
        });
    };

    loadSubAssemblies = () => {
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        return assemblyService.getAllSubAssembliesExpanded().then((subAssembliesResult) => {
            subAssemblies = subAssembliesResult.sort((a, b) => {
                if (a.subAssembly.subAssemblyId > b.subAssembly.subAssemblyId) return 1;
                if (b.subAssembly.subAssemblyId > a.subAssembly.subAssemblyId) return -1;
                return 0;
            });
            this.setState({subAssemblies});
        });
    };

    loadPartFamilies = () => {
        const partService = ServiceLocator.Instance.getPartService();
        return partService.getPartFamilies().then((partFamiliesResult) => {
            partFamilies = partFamiliesResult;
            this.setState({partFamilies}, () => {
                // Handle family selection based on saved state or auto-select first
                if (this.state.partFamily && partFamilies && partFamilies.length > 0) {
                    // Load the saved family's relationships
                    const savedFamily = partFamilies.find(x => x.id === this.state.partFamily);
                    if (savedFamily) {
                        partService.getPartFamilyRelationships(savedFamily.id).then((relationships) => {
                            this.setState({
                                selectedFamily: savedFamily,
                                partFamilyRelationships: relationships || [],
                                modifiedRelationships: {},
                                hasQuantityChanges: false
                            });
                        });
                    }
                } else if (!this.state.partFamily && partFamilies && partFamilies.length > 0) {
                    // Auto-select first family if none selected and we have families
                    const firstFamily = partFamilies[0];
                    partService.getPartFamilyRelationships(firstFamily.id).then((relationships) => {
                        this.setState({
                            partFamily: firstFamily.id,
                            selectedFamily: firstFamily,
                            partFamilyRelationships: relationships || [],
                            modifiedRelationships: {},
                            hasQuantityChanges: false
                        }, () => {
                            this.saveStateToStorage();
                        });
                    });
                }
            });
        });
    };

    loadAssemblyFamilies = () => {
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        return assemblyService.getAssemblyFamilies().then((assemblyFamiliesResult) => {
            assemblyFamilies = assemblyFamiliesResult;
            this.setState({assemblyFamilies});
        });
    };

    // Type Selection
    handleTypeChange = (event) => {
        this.setState({
            selectedType: event.target.value,
            // Reset family selections when type changes
            partFamily: null,
            selectedFamily: null,
            assemblyFamily: null,
            editingPartFamily: null,
            editingAssemblyFamily: null,
            partFamilyRelationships: [],
            modifiedRelationships: {},
            hasQuantityChanges: false
        }, () => {
            this.saveStateToStorage();
        });
    };

    // Part Family Methods
    handlePartFamilyChange = (event, newValue) => {
        const self = this;
        if (newValue && newValue.value) {
            const partService = ServiceLocator.Instance.getPartService();
            partService.getPartFamilyRelationships(newValue.value).then((relationships) => {
                self.setState({
                    partFamily: newValue.value,
                    selectedFamily: partFamilies.find(x => x.id === newValue.value),
                    partFamilyRelationships: relationships || [],
                    modifiedRelationships: {},
                    hasQuantityChanges: false,
                    showPartBulkEdit: false,
                    pendingParts: []
                }, () => {
                    this.saveStateToStorage();
                });
            });
        } else {
            self.setState({
                partFamily: null,
                selectedFamily: null,
                partFamilyRelationships: [],
                modifiedRelationships: {},
                hasQuantityChanges: false,
                showPartBulkEdit: false,
                pendingParts: []
            }, () => {
                this.saveStateToStorage();
            });
        }
    };

    changeNewPartFamilyName = (event) => {
        this.setState({newPartFamilyName: event.target.value});
    };

    changeNewPartFamilyDescription = (event) => {
        this.setState({newPartFamilyDescription: event.target.value});
    };

    changeNewPartFamilyNickname = (event) => {
        this.setState({newPartFamilyNickname: event.target.value});
    };

    changeNewPartFamilyImageURL = (event) => {
        this.setState({newPartFamilyImageURL: event.target.value});
    };

    editPartFamilyImageURL = (event) => {
        const editingPartFamily = this.state.editingPartFamily;
        editingPartFamily.imageURL = event.target.value;
        this.setState({editingPartFamily});
    };

    handlePartFamilyImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            window.$alertError("Please select a valid image file.");
            return;
        }

        // Validate file size (10MB limit)
        if (file.size > 10 * 1024 * 1024) {
            window.$alertError("File size must be less than 10MB.");
            return;
        }

        // For new families, we need to save the family first before uploading an image
        if (!this.state.editingPartFamily && !this.state.selectedFamily) {
            window.$alertError('Please save the family first before uploading an image.');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        try {
            // Determine which family we're uploading for
            const familyId = this.state.editingPartFamily ? this.state.editingPartFamily.id : this.state.selectedFamily?.id;
            
            const partService = ServiceLocator.Instance.getPartService();
            const response = await partService.uploadPartFamilyImage(familyId, file);

            // Set the uploaded image URL
            if (this.state.editingPartFamily) {
                const editingPartFamily = this.state.editingPartFamily;
                editingPartFamily.imageURL = response.imageUrl;
                this.setState({editingPartFamily});
            } else {
                // Update the selected family and reload
                this.loadPartFamilies();
            }

            window.$alertSuccess("Image uploaded successfully!");
        } catch (error) {
            console.error('Upload failed:', error);
            window.$alertError(error.response?.data?.error || 'Failed to upload image');
        }

        // Clear the file input
        event.target.value = '';
    };

    printPartFamilyLabel = () => {
        if (!this.state.selectedFamily) return;
        
        const family = this.state.selectedFamily;
        
        // Determine the main text to display (nickname if available, otherwise family name)
        const mainText = family.nickname || family.partFamilyId;
        const secondaryText = family.nickname ? family.partFamilyId : family.description;
        const descriptionText = family.nickname ? family.description : '';
        
        // Calculate font size for main text to fit in the available space
        // Available width is roughly 75% of 4 inches = 3 inches = 288px
        // Available height is roughly 2 inches = 192px
        const maxWidth = 288;
        const maxHeight = 192;
        const baseFontSize = 70;
        
        // Simple font size calculation based on text length
        let fontSize = baseFontSize;
        if (mainText.length > 5) {
            fontSize = Math.max(24, baseFontSize - Math.floor(mainText.length / 0.3));
        }
        
        // Create HTML for the label
        let containerHtml = '<div class=\'top-gutter\'></div><div class=\'left-gutter\'></div>';
        
        containerHtml += `<div class="label-standard">
            <div class="label-left">
                <div style="text-align: center;">
                    <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${family.id}" style="width: 75px; height: 75px;"/>
                    <div style="font-size: 11px; margin-top: 10px;">BOM Check</div>
                </div>
            </div>
            <div class="label-center">
                <div style="font-weight: 700; font-size: ${fontSize}px; line-height: 1.1;">${mainText}</div>
                <div style="font-size: 14px; margin-top: 5px;">${secondaryText}</div>
                ${descriptionText ? `<div style="font-size: 11px; margin-top: 3px;">${descriptionText}</div>` : ''}
            </div>
        </div>`;
        
        containerHtml += '<div class=\'right-gutter\'></div><div class=\'bottom-gutter\'></div>';
        
        // Open a new window and print the label
        const win = window.open("", "Title", "toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=no,width=384,height=1100");
        win.document.body.innerHTML = `
            <html>
                <head>
                    <style>
                        @media print { @page { margin: 0 !important; } body, html { margin: 0 !important; padding: 0 !important; } }
                        .label-left, .label-right {
                            width: 25%;
                            display: inline-block;
                            text-align: center;
                        }
                        .label-center {
                            width: 75%;
                            display: inline-block;
                            text-align: center;
                        }
                        .labels_page {
                            font-family: Montserrat, sans-serif;
                            width: 4in;
                            position: absolute;
                            top: 0;
                            left: 0;
                        }
                        .label-standard {
                            height: 2in;
                            text-align: center;
                            display: flex;
                            align-items: center;
                            position: relative;
                            padding: 0 10px;
                        }
                    </style>
                </head>
                <body>
                    <div id="container" class="labels_page">${containerHtml}</div>
                </body>
            </html>`;
        
        setTimeout(() => {
            win.print();
        }, 2000);
    };

    getFirstImageUrl = (imageURLs) => {
        if (!imageURLs) return null;
        const urls = imageURLs.split('\n').filter(url => url.trim());
        return urls.length > 0 ? urls[0] : null;
    };

    editPartFamilyId = (event) => {
        const editingPartFamily = this.state.editingPartFamily;
        editingPartFamily.partFamilyId = event.target.value;
        this.setState({editingPartFamily});
    };

    editPartFamilyDescription = (event) => {
        const editingPartFamily = this.state.editingPartFamily;
        editingPartFamily.description = event.target.value;
        this.setState({editingPartFamily});
    };

    editPartFamilyNickname = (event) => {
        const editingPartFamily = this.state.editingPartFamily;
        editingPartFamily.nickname = event.target.value;
        this.setState({editingPartFamily});
    };

    saveNewPartFamily = () => {
        const self = this;
        const partService = ServiceLocator.Instance.getPartService();
        
        // Check for duplicate family name
        const trimmedName = this.state.newPartFamilyName.trim();
        if (!trimmedName) {
            window.$alertError('Family name is required');
            return;
        }
        
        const existingFamily = this.state.partFamilies && this.state.partFamilies.find(
            family => family.partFamilyId.toLowerCase() === trimmedName.toLowerCase()
        );
        
        if (existingFamily) {
            window.$alertError(`A part family with the name "${trimmedName}" already exists`);
            return;
        }
        
        self.setState({savingNewValue: true});
        
        const payload = {
            id: uuid(),
            partFamilyId: trimmedName,
            description: this.state.newPartFamilyDescription,
            nickname: this.state.newPartFamilyNickname,
            imageURL: this.state.newPartFamilyImageURL
        };
        
        partService.createPartFamily(payload).then((createdFamily) => {
            self.loadPartFamilies().then(() => {
                // Auto-select the newly created family
                partService.getPartFamilyRelationships(createdFamily.id).then((relationships) => {
                    self.setState({
                        savingNewValue: false,
                        newPartFamilyOpen: false,
                        partFamily: createdFamily.id,
                        selectedFamily: createdFamily,
                        partFamilyRelationships: relationships || [],
                        newPartFamilyName: '',
                        newPartFamilyDescription: '',
                        newPartFamilyNickname: '',
                        newPartFamilyImageURL: ''
                    }, () => {
                        this.saveStateToStorage();
                    });
                    window.$alertSuccess('Part family created');
                });
            });
        }).catch((error) => {
            console.error('Error creating part family:', error);
            self.setState({savingNewValue: false});
            window.$alertError('Error creating part family');
        });
    };

    savePartFamily = () => {
        const self = this;
        const partService = ServiceLocator.Instance.getPartService();
        
        // Check for duplicate family name (excluding the current family being edited)
        const trimmedName = this.state.editingPartFamily.partFamilyId.trim();
        if (!trimmedName) {
            window.$alertError('Family name is required');
            return;
        }
        
        const existingFamily = this.state.partFamilies && this.state.partFamilies.find(
            family => family.id !== this.state.editingPartFamily.id && 
                     family.partFamilyId.toLowerCase() === trimmedName.toLowerCase()
        );
        
        if (existingFamily) {
            window.$alertError(`A part family with the name "${trimmedName}" already exists`);
            return;
        }
        
        self.setState({savingNewValue: true});
        partService.updatePartFamily(this.state.editingPartFamily).then(() => {
            self.loadPartFamilies().then(() => {
                self.setState({savingNewValue: false, editPartFamilyOpen: false});
                window.$alertSuccess('Part family updated');
            });
        });
    };

    handlePartChange = (event, newValue) => {
        this.setState({selectedPartForFamily: newValue});
    };

    addPartToFamily = () => {
        const self = this;
        const partService = ServiceLocator.Instance.getPartService();
        self.setState({savingNewValue: true});
        
        const payload = {
            partId: this.state.selectedPartForFamily.value,
            familyId: this.state.editingPartFamily.id,
            packageQuantity: parseInt(this.state.newPartToFamilyPackageQuantity) || 1,
            purchaseQuantity: parseInt(this.state.newPartToFamilyPurchaseQuantity) || 1,
            isImpliedPart: this.state.newPartToFamilyIsImpliedPart
        };

        partService.addPartToFamily(payload).then(() => {
            partService.getPartFamilyRelationships(this.state.editingPartFamily.id).then((relationships) => {
                self.setState({
                    partFamilyRelationships: relationships || [],
                    savingNewValue: false,
                    partToFamilyOpen: false,
                    selectedPartForFamily: null,
                    newPartToFamilyPackageQuantity: '',
                    newPartToFamilyPurchaseQuantity: '',
                    newPartToFamilyIsImpliedPart: false
                });
            });
        });
    };

    removePartFromFamily = (partId, familyId) => {
        const self = this;
        const partService = ServiceLocator.Instance.getPartService();
        const confirm = window.confirm('Are you sure you want to remove this part from the family?');
        if (!confirm) return;
        partService.removePartFromFamily(partId, familyId).then(() => {
            partService.getPartFamilyRelationships(familyId).then((relationships) => {
                self.setState({partFamilyRelationships: relationships || []});
                window.$alertSuccess('Part removed from family');
            });
        });
    };

    handleRelationshipQuantityChange = (relationship, field, value) => {
        const modifiedRelationships = {...this.state.modifiedRelationships};
        const key = `${relationship.partId}_${relationship.familyId}`;
        
        if (!modifiedRelationships[key]) {
            modifiedRelationships[key] = {...relationship};
        }
        
        // Handle boolean fields (like isImpliedPart) differently from numeric fields
        if (field === 'isImpliedPart') {
            modifiedRelationships[key][field] = value;
        } else {
            modifiedRelationships[key][field] = parseNumberInputValue(value);
        }
        
        this.setState({
            modifiedRelationships,
            hasQuantityChanges: true
        });
    };

    getCurrentRelationshipValue = (relationship, field) => {
        const key = `${relationship.partId}_${relationship.familyId}`;
        const modifiedRelationship = this.state.modifiedRelationships[key];
        
        if (modifiedRelationship && modifiedRelationship[field] !== undefined) {
            return modifiedRelationship[field];
        }
        
        return relationship[field];
    };

    saveAllQuantityChanges = () => {
        const self = this;
        const partService = ServiceLocator.Instance.getPartService();
        
        self.setState({savingNewValue: true});
        
        const updatePromises = Object.values(this.state.modifiedRelationships).map(relationship => 
            partService.updatePartFamilyRelationship(relationship)
        );
        
        Promise.all(updatePromises).then(() => {
            window.$alertSuccess('Quantity changes saved');
            // Reload the relationships to get the latest data
            if (this.state.selectedFamily) {
                partService.getPartFamilyRelationships(this.state.selectedFamily.id).then((relationships) => {
                    self.setState({
                        partFamilyRelationships: relationships || [],
                        modifiedRelationships: {},
                        hasQuantityChanges: false,
                        savingNewValue: false
                    });
                });
            }
        }).catch((error) => {
            console.error('Error saving quantity changes:', error);
            self.setState({savingNewValue: false});
        });
    };

    editPart = (part, newTab) => {
        if (newTab)
            window.open(`/admin/parts/${part.id}`);
        else
            globalProps.history.push(`/admin/parts/${part.id}`);
    };



    // Part Family Bulk Edit Methods
    addPartBulkLine = () => {
        const newLine = {
            id: uuid(),
            selectedPart: null,
            packageQuantity: 1,
            purchaseQuantity: 1,
            isImpliedPart: false,
            isComplete: false
        };
        
        this.setState(prevState => ({
            pendingParts: [...prevState.pendingParts, newLine]
        }));
    };

    handlePartBulkChange = (lineId, newValue) => {
        this.setState(prevState => {
            const pendingParts = prevState.pendingParts.map(line =>
                line.id === lineId
                    ? { ...line, selectedPart: newValue, isComplete: !!newValue }
                    : line
            );

            if (newValue) {
                const hasBlankLine = pendingParts.some(line => !line.selectedPart);
                if (!hasBlankLine) {
                    pendingParts.push({
                        id: uuid(),
                        selectedPart: null,
                        packageQuantity: 1,
                        purchaseQuantity: 1,
                        isImpliedPart: false,
                        isComplete: false
                    });
                }
            }

            return { pendingParts };
        });
    };

    handlePartBulkQuantityChange = (lineId, field, value) => {
        this.setState(prevState => ({
            pendingParts: prevState.pendingParts.map(line => 
                line.id === lineId 
                    ? { ...line, [field]: field === 'isImpliedPart' ? value : (parseInt(value) || 1) }
                    : line
            )
        }));
    };

    removePartBulkLine = (lineId) => {
        this.setState(prevState => ({
            pendingParts: prevState.pendingParts.filter(line => line.id !== lineId)
        }));
    };

    handlePartBulkKeyDown = (lineId, event) => {
        if (event.key === 'Tab' && !event.shiftKey) {
            
            
            // Find current line index
            const currentIndex = this.state.pendingParts.findIndex(line => line.id === lineId);
            
            // If this is the last line or current line is complete, add a new line
            if (currentIndex === this.state.pendingParts.length - 1 || 
                this.state.pendingParts[currentIndex].isComplete) {
                
                    event.preventDefault();
                this.addPartBulkLine();
                
                // Focus the new line's autocomplete after a brief delay
                setTimeout(() => {
                    const newLineId = this.state.pendingParts[this.state.pendingParts.length - 1]?.id;
                    if (newLineId) {
                        const autocompleteElement = document.querySelector(`[data-part-line-id="${newLineId}"] input`);
                        if (autocompleteElement) {
                            autocompleteElement.focus();
                        }
                    }
                }, 100);
            }
        }
    };

    saveAllPartBulkChanges = async () => {
        const self = this;
        const partService = ServiceLocator.Instance.getPartService();
        
        // Check if a family is selected
        if (!this.state.selectedFamily) {
            window.$alertError('Please select a family before adding parts.');
            return;
        }
        
        self.setState({savingNewValue: true});
        
        try {
            // Debug: Log the pending parts and selected parts
            console.log('Pending parts:', this.state.pendingParts);
            console.log('Available parts:', this.state.parts);
            
            // Get all parts that have been selected
            const partsToAdd = this.state.pendingParts
                .filter(line => line.selectedPart)
                .map(line => {
                    console.log('Processing line:', line);
                    console.log('Selected part:', line.selectedPart);
                    
                    // Verify the part exists in our parts list
                    const partExists = this.state.parts.find(p => p.part.id === line.selectedPart.value);
                    if (!partExists) {
                        console.error('Part not found in parts list:', line.selectedPart.value);
                        throw new Error(`Part with ID ${line.selectedPart.value} not found in parts list`);
                    }
                    
                    return {
                        partId: line.selectedPart.value,
                        familyId: this.state.selectedFamily.id,
                        packageQuantity: line.packageQuantity,
                        purchaseQuantity: line.purchaseQuantity,
                        isImpliedPart: line.isImpliedPart || false
                    };
                });
            
            if (partsToAdd.length === 0) {
                self.setState({savingNewValue: false});
                return;
            }
            
            // Add all parts to the family
            for (const partData of partsToAdd) {
                await partService.addPartToFamily(partData);
            }
            
            // Reload the relationships to get the latest data
            const relationships = await partService.getPartFamilyRelationships(this.state.selectedFamily.id);
            
            self.setState({
                partFamilyRelationships: relationships || [],
                savingNewValue: false,
                showPartBulkEdit: false,
                pendingParts: []
            });
            
            window.$alertSuccess(`${partsToAdd.length} part${partsToAdd.length > 1 ? 's' : ''} added to family`);
        } catch (error) {
            console.error('Error adding parts to family:', error);
            self.setState({savingNewValue: false});
            window.$alertError('Error adding parts to family');
        }
    };

    cancelPartBulkEdit = () => {
        this.setState({
            showPartBulkEdit: false,
            pendingParts: []
        });
    };

    // Assembly Family Bulk Edit Methods
    addAssemblyBulkLine = () => {
        const newLine = {
            id: uuid(),
            selectedAssembly: null,
            isComplete: false
        };
        
        this.setState(prevState => ({
            pendingAssemblies: [...prevState.pendingAssemblies, newLine]
        }));
    };

    handleAssemblyBulkChange = (lineId, newValue) => {
        this.setState(prevState => {
            const pendingAssemblies = prevState.pendingAssemblies.map(line =>
                line.id === lineId
                    ? { ...line, selectedAssembly: newValue, isComplete: !!newValue }
                    : line
            );

            if (newValue) {
                const hasBlankLine = pendingAssemblies.some(line => !line.selectedAssembly);
                if (!hasBlankLine) {
                    pendingAssemblies.push({
                        id: uuid(),
                        selectedAssembly: null,
                        isComplete: false
                    });
                }
            }

            return { pendingAssemblies };
        });
    };

    removeAssemblyBulkLine = (lineId) => {
        this.setState(prevState => ({
            pendingAssemblies: prevState.pendingAssemblies.filter(line => line.id !== lineId)
        }));
    };

    handleAssemblyBulkKeyDown = (lineId, event) => {
        if (event.key === 'Tab' && !event.shiftKey) {
            event.preventDefault();
            
            // Find current line index
            const currentIndex = this.state.pendingAssemblies.findIndex(line => line.id === lineId);
            
            // If this is the last line or current line is complete, add a new line
            if (currentIndex === this.state.pendingAssemblies.length - 1 || 
                this.state.pendingAssemblies[currentIndex].isComplete) {
                this.addAssemblyBulkLine();
                
                // Focus the new line's autocomplete after a brief delay
                setTimeout(() => {
                    const newLineId = this.state.pendingAssemblies[this.state.pendingAssemblies.length - 1]?.id;
                    if (newLineId) {
                        const autocompleteElement = document.querySelector(`[data-line-id="${newLineId}"] input`);
                        if (autocompleteElement) {
                            autocompleteElement.focus();
                        }
                    }
                }, 100);
            } else {
                // Focus the next line
                const nextLineId = this.state.pendingAssemblies[currentIndex + 1]?.id;
                if (nextLineId) {
                    const autocompleteElement = document.querySelector(`[data-line-id="${nextLineId}"] input`);
                    if (autocompleteElement) {
                        autocompleteElement.focus();
                    }
                }
            }
        }
    };

    saveAllAssemblyBulkChanges = async () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        self.setState({savingNewValue: true});
        
        try {
            // Get all assemblies that have been selected
            const assembliesToAdd = this.state.pendingAssemblies
                .filter(line => line.selectedAssembly)
                .map(line => line.selectedAssembly.value);
            
            if (assembliesToAdd.length === 0) {
                self.setState({savingNewValue: false});
                return;
            }
            
            // Check if editingAssemblyFamily is set
            if (!this.state.editingAssemblyFamily) {
                window.$alertError('Please select an assembly family first');
                self.setState({savingNewValue: false});
                return;
            }
            
            // Get the current assemblies in the family to calculate the next order
            const familyAssemblies = this.state.assemblies.filter(x => 
                x.assembly.isActive && 
                x.assembly.assemblyFamilyId === this.state.editingAssemblyFamily.id
            );
            const maxOrder = familyAssemblies.length > 0 
                ? Math.max(...familyAssemblies.map(a => a.assembly.assemblyFamilyOrder || 0))
                : -1;
            
            // Add all assemblies to the family with proper ordering
            for (let i = 0; i < assembliesToAdd.length; i++) {
                const assemblyId = assembliesToAdd[i];
                const nextOrder = maxOrder + 1 + i;
                
                const assemblyExpanded = this.state.assemblies.find(x => x.assembly.id === assemblyId);
                if (!assemblyExpanded) {
                    console.error(`Assembly with ID ${assemblyId} not found in state`);
                    throw new Error(`Assembly with ID ${assemblyId} not found`);
                }
                const assemblyToUpdate = assemblyExpanded.assembly;
                
                // Create a clean update payload without the nested assemblyFamily object
                const updatePayload = {
                    id: assemblyToUpdate.id,
                    assemblyType: assemblyToUpdate.assemblyType,
                    manufacturerFor: assemblyToUpdate.manufacturerFor,
                    tertiaryType: assemblyToUpdate.tertiaryType,
                    assemblyId: assemblyToUpdate.assemblyId,
                    revision: assemblyToUpdate.revision,
                    description: assemblyToUpdate.description,
                    squarespaceURL: assemblyToUpdate.squarespaceURL,
                    labelDescription: assemblyToUpdate.labelDescription,
                    installVideoURL: assemblyToUpdate.installVideoURL,
                    weightPounds: assemblyToUpdate.weightPounds,
                    weightOunces: assemblyToUpdate.weightOunces,
                    imageURL: assemblyToUpdate.imageURL,
                    fileURLs: this.normalizeFileURLsForPayload(assemblyToUpdate.fileURLs),
                    revisionNotes: assemblyToUpdate.revisionNotes,
                    status: assemblyToUpdate.status,
                    isActive: assemblyToUpdate.isActive,
                    shippingCost: assemblyToUpdate.shippingCost,
                    msrp: assemblyToUpdate.msrp,
                    discountTier1: assemblyToUpdate.discountTier1,
                    discountTier2: assemblyToUpdate.discountTier2,
                    discountTier3: assemblyToUpdate.discountTier3,
                    spareBushingsSetId: assemblyToUpdate.spareBushingsSetId,
                    spareBushingsSetQuantity: assemblyToUpdate.spareBushingsSetQuantity,
                    spareHardwareSetQuantity: assemblyToUpdate.spareHardwareSetQuantity,
                    assemblyFamilyId: this.state.editingAssemblyFamily.id,
                    assemblyFamilyOrder: nextOrder,
                    currentlyPackaging: assemblyToUpdate.currentlyPackaging,
                    isInventoryItem: assemblyToUpdate.isInventoryItem,
                    partsToAssemblies: assemblyToUpdate.partsToAssemblies || [],
                    subAssembliesToAssemblies: assemblyToUpdate.subAssembliesToAssemblies || [],
                    ...('customerProductTabsJson' in assemblyToUpdate
                        ? { customerProductTabsJson: assemblyToUpdate.customerProductTabsJson }
                        : {})
                };
                
                await assemblyService.updateAssembly(updatePayload);
            }

            // Reload assemblies so family table reflects the new relationships immediately.
            await self.loadAssemblies();
            
            self.setState({
                savingNewValue: false,
                showAssemblyBulkEdit: false,
                pendingAssemblies: []
            });
            
            window.$alertSuccess(`${assembliesToAdd.length} assembl${assembliesToAdd.length > 1 ? 'ies' : 'y'} added to family`);
        } catch (error) {
            console.error('Error adding assemblies to family:', error);
            self.setState({savingNewValue: false});
            window.$alertError('Error adding assemblies to family');
        }
    };

    cancelAssemblyBulkEdit = () => {
        this.setState({
            showAssemblyBulkEdit: false,
            pendingAssemblies: []
        });
    };

    // Assembly Family Methods
    handleAssemblyFamilyChange = (event, newValue) => {
        this.setState({
            assemblyFamily: newValue ? newValue.value : null,
            editingAssemblyFamily: newValue && newValue.value 
                ? assemblyFamilies.find(x => x.id === newValue.value) 
                : null,
            showAssemblyBulkEdit: false,
            pendingAssemblies: []
        }, () => {
            this.saveStateToStorage();
        });
    };

    changeNewFamilyName = (event) => {
        this.setState({newFamilyName: event.target.value});
    };

    changeNewFamilyDescription = (event) => {
        this.setState({newFamilyDescription: event.target.value});
    };

    saveNewAssemblyFamily = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        
        // Check for duplicate family name
        const trimmedName = this.state.newFamilyName.trim();
        if (!trimmedName) {
            window.$alertError('Family name is required');
            return;
        }
        
        const existingFamily = this.state.assemblyFamilies && this.state.assemblyFamilies.find(
            family => family.assemblyFamilyId.toLowerCase() === trimmedName.toLowerCase()
        );
        
        if (existingFamily) {
            window.$alertError(`An assembly family with the name "${trimmedName}" already exists`);
            return;
        }
        
        self.setState({savingNewValue: true});
        
        const payload = {
            id: uuid(),
            assemblyFamilyId: trimmedName,
            description: this.state.newFamilyDescription
        };
        
        assemblyService.createAssemblyFamily(payload).then((createdFamily) => {
            self.loadAssemblyFamilies().then(() => {
                // Auto-select the newly created family
                self.setState({
                    savingNewValue: false,
                    newAssemblyFamilyOpen: false,
                    assemblyFamily: createdFamily.id,
                    editingAssemblyFamily: createdFamily,
                    newFamilyName: '',
                    newFamilyDescription: ''
                }, () => {
                    this.saveStateToStorage();
                });
                window.$alertSuccess('Assembly family created');
            });
        }).catch((error) => {
            console.error('Error creating assembly family:', error);
            self.setState({savingNewValue: false});
            window.$alertError('Error creating assembly family');
        });
    };

    editAssemblyFamilyId = (event) => {
        const editingAssemblyFamily = {...this.state.editingAssemblyFamily};
        editingAssemblyFamily.assemblyFamilyId = event.target.value;
        this.setState({editingAssemblyFamily});
    };

    editAssemblyFamilyDescription = (event) => {
        const editingAssemblyFamily = {...this.state.editingAssemblyFamily};
        editingAssemblyFamily.description = event.target.value;
        this.setState({editingAssemblyFamily});
    };

    saveAssemblyFamily = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        
        // Check if editingAssemblyFamily is set
        if (!this.state.editingAssemblyFamily) {
            window.$alertError('No assembly family selected for editing');
            return;
        }
        
        // Check for duplicate family name (excluding the current family being edited)
        const trimmedName = this.state.editingAssemblyFamily.assemblyFamilyId.trim();
        if (!trimmedName) {
            window.$alertError('Family name is required');
            return;
        }
        
        const existingFamily = this.state.assemblyFamilies && this.state.assemblyFamilies.find(
            family => family.id !== this.state.editingAssemblyFamily.id && 
                     family.assemblyFamilyId.toLowerCase() === trimmedName.toLowerCase()
        );
        
        if (existingFamily) {
            window.$alertError(`An assembly family with the name "${trimmedName}" already exists`);
            return;
        }
        
        self.setState({savingNewValue: true});
        assemblyService.updateAssemblyFamily(this.state.editingAssemblyFamily).then(() => {
            self.loadAssemblyFamilies().then(() => {
                self.setState({savingNewValue: false, editAssemblyFamilyOpen: false});
                window.$alertSuccess('Assembly family updated');
            });
        });
    };

    handleAssemblyChange = (event, newValue) => {
        this.setState({selectedAssemblyForFamily: newValue ? newValue.value : ''});
    };

    addAssemblyToFamily = async () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        
        // Check if editingAssemblyFamily is set
        if (!this.state.editingAssemblyFamily) {
            window.$alertError('Please select an assembly family first');
            return;
        }
        
        self.setState({savingNewValue: true});
        
        try {
            // Find the assembly and update it to add to family
            const assembly = this.state.assemblies.find(x => x.assembly.id === this.state.selectedAssemblyForFamily).assembly;
            
            // Get the next available order for this family
            const familyAssemblies = this.state.assemblies.filter(x => 
                x.assembly.isActive && 
                x.assembly.assemblyFamilyId === this.state.editingAssemblyFamily.id
            );
            const maxOrder = familyAssemblies.length > 0 
                ? Math.max(...familyAssemblies.map(a => a.assembly.assemblyFamilyOrder || 0))
                : -1;
            const nextOrder = maxOrder + 1;
            
            // Create a clean update payload without the nested assemblyFamily object
            const updatePayload = {
                id: assembly.id,
                assemblyType: assembly.assemblyType,
                manufacturerFor: assembly.manufacturerFor,
                tertiaryType: assembly.tertiaryType,
                assemblyId: assembly.assemblyId,
                revision: assembly.revision,
                description: assembly.description,
                squarespaceURL: assembly.squarespaceURL,
                labelDescription: assembly.labelDescription,
                installVideoURL: assembly.installVideoURL,
                weightPounds: assembly.weightPounds,
                weightOunces: assembly.weightOunces,
                imageURL: assembly.imageURL,
                fileURLs: this.normalizeFileURLsForPayload(assembly.fileURLs),
                revisionNotes: assembly.revisionNotes,
                status: assembly.status,
                isActive: assembly.isActive,
                shippingCost: assembly.shippingCost,
                msrp: assembly.msrp,
                discountTier1: assembly.discountTier1,
                discountTier2: assembly.discountTier2,
                discountTier3: assembly.discountTier3,
                spareBushingsSetId: assembly.spareBushingsSetId,
                spareBushingsSetQuantity: assembly.spareBushingsSetQuantity,
                spareHardwareSetQuantity: assembly.spareHardwareSetQuantity,
                assemblyFamilyId: this.state.editingAssemblyFamily.id,
                assemblyFamilyOrder: nextOrder,
                currentlyPackaging: assembly.currentlyPackaging,
                isInventoryItem: assembly.isInventoryItem,
                partsToAssemblies: assembly.partsToAssemblies || [],
                subAssembliesToAssemblies: assembly.subAssembliesToAssemblies || [],
                ...('customerProductTabsJson' in assembly
                    ? { customerProductTabsJson: assembly.customerProductTabsJson }
                    : {})
            };
            
            await assemblyService.updateAssembly(updatePayload);
            await self.loadAssemblies();
            self.setState({savingNewValue: false, assemblyToFamilyOpen: false});
        } catch (error) {
            console.error('Error adding assembly to family:', error);
            self.setState({savingNewValue: false});
        }
    };

    removeAssemblyFromFamily = async (assemblyId) => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        const confirm = window.confirm('Are you sure you want to remove this assembly from the family?');
        if (!confirm) return;
        
        try {
            // Find the assembly and update it to remove from family
            const assembly = this.state.assemblies.find(x => x.assembly.id === assemblyId).assembly;
            // Create a clean update payload without the nested assemblyFamily object
            const updatePayload = {
                id: assembly.id,
                assemblyType: assembly.assemblyType,
                manufacturerFor: assembly.manufacturerFor,
                tertiaryType: assembly.tertiaryType,
                assemblyId: assembly.assemblyId,
                revision: assembly.revision,
                description: assembly.description,
                squarespaceURL: assembly.squarespaceURL,
                labelDescription: assembly.labelDescription,
                installVideoURL: assembly.installVideoURL,
                weightPounds: assembly.weightPounds,
                weightOunces: assembly.weightOunces,
                imageURL: assembly.imageURL,
                fileURLs: this.normalizeFileURLsForPayload(assembly.fileURLs),
                revisionNotes: assembly.revisionNotes,
                status: assembly.status,
                isActive: assembly.isActive,
                shippingCost: assembly.shippingCost,
                msrp: assembly.msrp,
                discountTier1: assembly.discountTier1,
                discountTier2: assembly.discountTier2,
                discountTier3: assembly.discountTier3,
                spareBushingsSetId: assembly.spareBushingsSetId,
                spareBushingsSetQuantity: assembly.spareBushingsSetQuantity,
                spareHardwareSetQuantity: assembly.spareHardwareSetQuantity,
                assemblyFamilyId: null,
                assemblyFamilyOrder: null,
                currentlyPackaging: assembly.currentlyPackaging,
                isInventoryItem: assembly.isInventoryItem,
                partsToAssemblies: assembly.partsToAssemblies || [],
                subAssembliesToAssemblies: assembly.subAssembliesToAssemblies || [],
                ...('customerProductTabsJson' in assembly
                    ? { customerProductTabsJson: assembly.customerProductTabsJson }
                    : {})
            };
            
            await assemblyService.updateAssembly(updatePayload);
            await self.loadAssemblies();
            window.$alertSuccess('Assembly removed from family');
        } catch (error) {
            console.error('Error removing assembly from family:', error);
        }
    };

    editAssembly = (assemblyExpanded, newTab) => {
        if (newTab)
            window.open(`/admin/assemblies/${assemblyExpanded.assembly ? assemblyExpanded.assembly.id : assemblyExpanded.subAssembly.id}`);
        else
            globalProps.history.push(`/admin/assemblies/${assemblyExpanded.assembly ? assemblyExpanded.assembly.id : assemblyExpanded.subAssembly.id}`);
    };

    deletePartFamily = () => {
        const self = this;
        const partService = ServiceLocator.Instance.getPartService();
        const familyName = this.state.selectedFamily.partFamilyId;
        
        const confirm = window.confirm(`Are you sure you want to delete the Part Family "${familyName}"? This action cannot be undone and will remove all parts from this family.`);
        if (!confirm) return;
        
        self.setState({savingNewValue: true});
        
        partService.deletePartFamily(this.state.selectedFamily.id).then(() => {
            self.loadPartFamilies().then(() => {
                self.setState({
                    savingNewValue: false,
                    partFamily: null,
                    selectedFamily: null,
                    partFamilyRelationships: []
                }, () => {
                    this.saveStateToStorage();
                });
                window.$alertSuccess('Part family deleted');
            });
        }).catch((error) => {
            console.error('Error deleting part family:', error);
            self.setState({savingNewValue: false});
            window.$alertError('Error deleting part family');
        });
    };

    deleteAssemblyFamily = () => {
        if (this.state.assemblyFamily && window.confirm('Are you sure you want to delete this assembly family?')) {
            const assemblyService = ServiceLocator.Instance.getAssemblyService();
            assemblyService.deleteAssemblyFamily(this.state.assemblyFamily).then(() => {
                this.loadAssemblyFamilies();
                this.setState({assemblyFamily: null}, () => {
                    this.saveStateToStorage();
                });
            });
        }
    };

    onDragEnd = (result) => {
        if (!result.destination) return;

        const { source, destination } = result;
        
        if (this.state.selectedType === 'Parts' && this.state.partFamily) {
            this.handlePartFamilyDragEnd(source, destination);
        } else if (this.state.selectedType === 'Assemblies' && this.state.assemblyFamily) {
            this.handleAssemblyFamilyDragEnd(source, destination);
        }
    };

    handlePartFamilyDragEnd = async (source, destination) => {
        const relationships = this.state.partFamilyRelationships || [];
        const reorderedRelationships = Array.from(relationships);
        const [removed] = reorderedRelationships.splice(source.index, 1);
        reorderedRelationships.splice(destination.index, 0, removed);

        // Update the order values in the reordered relationships
        const updatedRelationships = reorderedRelationships.map((relationship, index) => ({
            ...relationship,
            order: index
        }));

        // Update the order in the state with the correct order values
        this.setState({
            partFamilyRelationships: updatedRelationships
        });

        // Send the new order to the API
        const partIds = updatedRelationships.map(r => r.partId);
        const partService = ServiceLocator.Instance.getPartService();
        try {
            await partService.updatePartFamilyOrder(this.state.partFamily, partIds);
        } catch (error) {
            console.error('Error updating part family order:', error);
            // Revert the state if the API call fails
            this.loadPartFamilies();
        }
    };

    handleAssemblyFamilyDragEnd = async (source, destination) => {
        // Get the assemblies that are currently displayed (filtered and sorted)
        const displayedAssemblies = this.state.assemblies?.filter(x => x.assembly.isActive && x.assembly.assemblyFamilyId === this.state.assemblyFamily) || [];
        const sortedAssemblies = displayedAssemblies.sort((a, b) => (a.assembly.assemblyFamilyOrder || 0) - (b.assembly.assemblyFamilyOrder || 0));
        
        console.log('Drag and drop:', { source: source.index, destination: destination.index });
        console.log('Sorted assemblies before reorder:', sortedAssemblies.map(a => ({ id: a.assembly.id, assemblyId: a.assembly.assemblyId, order: a.assembly.assemblyFamilyOrder })));
        
        // Reorder the assemblies based on drag and drop
        const reorderedAssemblies = Array.from(sortedAssemblies);
        const [removed] = reorderedAssemblies.splice(source.index, 1);
        reorderedAssemblies.splice(destination.index, 0, removed);
        
        console.log('Reordered assemblies:', reorderedAssemblies.map(a => ({ id: a.assembly.id, assemblyId: a.assembly.assemblyId, order: a.assembly.assemblyFamilyOrder })));

        // Update the order in the state
        const updatedAssemblies = this.state.assemblies?.map(assembly => {
            const reorderedAssembly = reorderedAssemblies.find(a => a.assembly.id === assembly.assembly.id);
            if (reorderedAssembly) {
                return {
                    ...assembly,
                    assembly: {
                        ...assembly.assembly,
                        assemblyFamilyOrder: reorderedAssemblies.indexOf(reorderedAssembly)
                    }
                };
            }
            return assembly;
        });

        this.setState({
            assemblies: updatedAssemblies
        });

        // Send the new order to the API using AssemblyService
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        try {
            // Update each assembly with its new order
            for (let i = 0; i < reorderedAssemblies.length; i++) {
                const assembly = reorderedAssemblies[i].assembly;
                const updatedAssembly = {
                    ...assembly,
                    assemblyFamilyOrder: i
                };
                console.log(`Setting assembly ${assembly.assemblyId} to order ${i}`);
                await assemblyService.updateAssembly(updatedAssembly);
            }
            
            // Force clear the cache and reload assemblies to ensure the order is correct
            localStorage.removeItem('assemblies');
            localStorage.removeItem('assembliesUpdated');
            await this.loadAssemblies();
        } catch (error) {
            console.error('Error updating assembly family order:', error);
            // Revert the state if the API call fails
            this.loadAssemblies();
        }
    };

    render() {
        const {classes} = this.props;
        
        if (this.state.loading) {
            return (
                <DashboardTemplate title="Families" onSyncDatabase={this.loadData}>
                    <div className="main-dashboard-root">
                        <CircularProgress />
                    </div>
                </DashboardTemplate>
            );
        }

        // Part Family Options
        let partFamilyOptions = [];
        if (this.state.partFamilies) {
            partFamilyOptions = this.state.partFamilies
                .map((partFamily) => ({title: partFamily.partFamilyId, value: partFamily.id}))
                .sort((a, b) => a.title.localeCompare(b.title));
        }

        // Assembly Family Options
        let assemblyFamilyOptions = [];
        if (this.state.assemblyFamilies) {
            assemblyFamilyOptions = this.state.assemblyFamilies
                .map((assemblyFamily) => ({title: assemblyFamily.assemblyFamilyId, value: assemblyFamily.id}))
                .sort((a, b) => a.title.localeCompare(b.title));
        }

        // Assembly Options for adding to family
        const assemblyOptions = this.state.assemblies ? this.state.assemblies.filter(x => x.assembly.isActive && 
            (!this.state.editingAssemblyFamily || x.assembly.assemblyFamilyId !== this.state.editingAssemblyFamily.id)
        ).map(x => ({
            type: 'assembly',
            partId: x.assembly.assemblyId,
            revision: x.assembly.revision,
            description: x.assembly.description,
            value: x.assembly.id
        })) : [];

        return (
            <DashboardTemplate title="Families" onSyncDatabase={this.loadData}>
                <div className="main-dashboard-root">
                    <div className="container" style={{maxWidth: 1200}}>
                        <div className={classes.toolbar} style={{maxWidth: '100%'}}>
                            <div className={classes.toolbarLeft}>
                                <Select
                                    id="ddlType"
                                    style={{ minWidth: 120, marginRight: 20 }}
                                    value={this.state.selectedType}
                                    onChange={this.handleTypeChange}
                                    variant="outlined"
                                    label="Type"
                                >
                                    <MenuItem value="Parts">Parts</MenuItem>
                                    <MenuItem value="Assemblies">Assemblies</MenuItem>
                                </Select>
                                
                                {this.state.selectedType === 'Parts' && (
                                    <Autocomplete autoHighlight
                                        id="ddlPartFamily"
                                        style={{ minWidth: 250 }}
                                        options={partFamilyOptions}
                                        onChange={this.handlePartFamilyChange}
                                        value={this.state.partFamily ? partFamilyOptions.find(opt => opt.value === this.state.partFamily) : null}
                                        getOptionLabel={(option) => option.title}
                                        getOptionSelected={(option) => option.value === this.state.partFamily}
                                        className="autocomplete-dropdown"
                                        renderInput={(params) => <TextField {...params} inputProps={{
                                            ...params.inputProps
                                        }} label="Part Family" autoComplete="new-password" variant="outlined"/>}
                                    />
                                )}
                                
                                {this.state.selectedType === 'Assemblies' && (
                                    <Autocomplete autoHighlight
                                        id="ddlAssemblyFamily"
                                        style={{ minWidth: 250 }}
                                        options={assemblyFamilyOptions}
                                        onChange={this.handleAssemblyFamilyChange}
                                        value={this.state.assemblyFamily ? assemblyFamilyOptions.find(opt => opt.value === this.state.assemblyFamily) : null}
                                        getOptionLabel={(option) => option.title}
                                        getOptionSelected={(option) => option.value === this.state.assemblyFamily}
                                        className="autocomplete-dropdown"
                                        renderInput={(params) => <TextField {...params} inputProps={{
                                            ...params.inputProps
                                        }} label="Assembly Family" autoComplete="new-password" variant="outlined"/>}
                                    />
                                )}
                            </div>
                            
                            <div className={classes.toolbarRight}>
                                {this.state.selectedType === 'Parts' && (
                                    <>
                                        <Button 
                                            onClick={() => this.setState({newPartFamilyOpen: true})}
                                            primary={true}
                                            className="success-button-lg"
                                        >New Family</Button>
                                        {this.state.partFamily && (
                                            <>
                                                <Button 
                                                    onClick={() => this.setState({
                                                        editPartFamilyOpen: true,
                                                        editingPartFamily: this.state.selectedFamily,
                                                        editingPartFamilyOriginal: JSON.parse(JSON.stringify(this.state.selectedFamily))
                                                    })}
                                                    primary={true}
                                                    className="success-button-lg"
                                                >Edit Family</Button>
                                                <Button
                                                    onClick={this.printPartFamilyLabel}
                                                    primary={true} className="success-button-lg"
                                                >Print Labels</Button>
                                                <Button 
                                                    onClick={this.deletePartFamily}
                                                    primary={false}
                                                    className="delete-button-lg"
                                                >Delete Family</Button>
                                            </>
                                        )}
                                    </>
                                )}
                                
                                {this.state.selectedType === 'Assemblies' && (
                                    <>
                                        <Button 
                                            onClick={() => this.setState({newAssemblyFamilyOpen: true})}
                                            primary={true}
                                            className="success-button-lg"
                                        >New Family</Button>
                                        {this.state.assemblyFamily && (
                                            <>
                                                <Button 
                                                    label="Edit Family"
                                                    onClick={() => {
                                                        const selectedAssemblyFamily = this.state.assemblyFamilies.find(x => x.id === this.state.assemblyFamily);
                                                        this.setState({
                                                            editAssemblyFamilyOpen: true,
                                                            editingAssemblyFamily: selectedAssemblyFamily,
                                                            editingAssemblyFamilyOriginal: JSON.parse(JSON.stringify(selectedAssemblyFamily))
                                                        });
                                                    }}
                                                    primary={true}
                                                    className="success-button-lg"
                                                >Edit Family</Button>
                                                <Button 
                                                    onClick={this.deleteAssemblyFamily}
                                                    primary={false}
                                                    className="delete-button-lg"
                                                >Delete Family</Button>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {this.state.selectedType === 'Parts' && (
                            <div>
                                {this.state.partFamily && (
                                    <div>
                                        {/* Part Family Image Display */}
                                        {this.state.selectedFamily && this.state.selectedFamily.imageURL && (
                                            <img
                                                src={this.state.selectedFamily.imageURL}
                                                alt={this.state.selectedFamily.partFamilyId}
                                                style={{
                                                    maxWidth: '200px',
                                                    maxHeight: '150px',
                                                    objectFit: 'contain',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px'
                                                }}
                                            />
                                        )}
                                        <h3>{this.state.partFamilies && this.state.partFamilies.find(x => x.id === this.state.partFamily) ? this.state.partFamilies.find(x => x.id === this.state.partFamily).description : ''}</h3>
                                        {this.state.selectedFamily && this.state.selectedFamily.nickname && (
                                            <h4 style={{ color: '#666', marginTop: '-10px', marginBottom: '20px' }}>
                                                {this.state.selectedFamily.nickname}
                                            </h4>
                                        )}
                                        <div className="parts-table-container">
                                            <DragDropContext onDragEnd={this.onDragEnd}>
                                                <Droppable droppableId="part-family-table">
                                                    {(provided) => (
                                                        <table className="parts-table no-sticky-headers" ref={provided.innerRef} {...provided.droppableProps}>
                                                            <thead>
                                                            <tr>
                                                                <th style={{width: 50}}>Order</th>
                                                                <th style={{width: 50}}>Remove</th>
                                                                <th style={{minWidth: 120}}>Part</th>
                                                                <th style={{minWidth: 80}}>Package Qty</th>
                                                                <th style={{minWidth: 80}}>Purchase Qty</th>
                                                                <th style={{minWidth: 80}}>Implied Part</th>
                                                            </tr>
                                                            </thead>
                                                            <tbody>
                                                            {this.state.partFamilyRelationships && this.state.partFamilyRelationships
                                                                .sort((a, b) => (a.order || 0) - (b.order || 0))
                                                                .map((relationship, index) => {
                                                                const part = this.state.parts && this.state.parts.find(x => x.part.id === relationship.partId) ? this.state.parts.find(x => x.part.id === relationship.partId).part : null;
                                                                if (!part) return null;
                                                                return (
                                                                    <Draggable key={relationship.partId} draggableId={relationship.partId.toString()} index={index}>
                                                                        {(provided) => (
                                                                            <tr ref={provided.innerRef} {...provided.draggableProps} style={this.state.permissions.includes('EditAssembliesAndParts') ? {
                                                                                cursor: 'pointer',
                                                                                opacity: part.isActive ? 1 : 0.6,
                                                                                ...provided.draggableProps.style
                                                                            } : {}}>
                                                                                <td {...provided.dragHandleProps}>
                                                                                    <FontAwesomeIcon icon={faGripVertical} />
                                                                                </td>
                                                                                <td>
                                                                                    <FontAwesomeIcon 
                                                                                        icon={faTrash} 
                                                                                        size="1x"
                                                                                        style={{
                                                                                            width: 28,
                                                                                            fontSize: 18,
                                                                                            cursor: 'pointer'
                                                                                        }}
                                                                                        onClick={() => this.removePartFromFamily(relationship.partId, relationship.familyId)}
                                                                                    />
                                                                                </td>
                                                                                <td className="text-left">
                                                                                    {this.state.permissions.includes('EditAssembliesAndParts') ? (
                                                                                        <div style={{cursor: 'pointer'}} onClick={(e) => this.editPart(part, e.ctrlKey)}>
                                                                                            <PartDisplay 
                                                                                                part={part}
                                                                                                variant="table"
                                                                                                classes={this.props.classes}
                                                                                            />
                                                                                        </div>
                                                                                    ) : (
                                                                                        <PartDisplay 
                                                                                            part={part}
                                                                                            variant="table"
                                                                                            classes={this.props.classes}
                                                                                        />
                                                                                    )}
                                                                                </td>
                                                                                <td>
                                                                                    <TextField
                                                                                        type="number"
                                                                                        value={this.getCurrentRelationshipValue(relationship, 'packageQuantity')}
                                                                                        onChange={(e) => this.handleRelationshipQuantityChange(
                                                                                            relationship,
                                                                                            'packageQuantity',
                                                                                            e.target.value
                                                                                        )}
                                                                                        variant="outlined"
                                                                                        size="small"
                                                                                        inputProps={{ min: 0 }}
                                                                                        style={{width: '70px'}}
                                                                                    />
                                                                                </td>
                                                                                <td>
                                                                                    <TextField
                                                                                        type="number"
                                                                                        value={this.getCurrentRelationshipValue(relationship, 'purchaseQuantity')}
                                                                                        onChange={(e) => this.handleRelationshipQuantityChange(
                                                                                            relationship,
                                                                                            'purchaseQuantity',
                                                                                            e.target.value
                                                                                        )}
                                                                                        variant="outlined"
                                                                                        size="small"
                                                                                        inputProps={{ min: 0 }}
                                                                                        style={{width: '70px'}}
                                                                                    />
                                                                                </td>
                                                                                <td style={{textAlign: 'center'}}>
                                                                                    <Checkbox
                                                                                        checked={this.getCurrentRelationshipValue(relationship, 'isImpliedPart') || false}
                                                                                        onChange={(e) => this.handleRelationshipQuantityChange(
                                                                                            relationship,
                                                                                            'isImpliedPart',
                                                                                            e.target.checked
                                                                                        )}
                                                                                        size="small"
                                                                                        color="primary"
                                                                                    />
                                                                                </td>
                                                                            </tr>
                                                                        )}
                                                                    </Draggable>
                                                                );
                                                            })}
                                                            {provided.placeholder}
                                                            {this.state.showPartBulkEdit && this.state.pendingParts.map((line, index) => (
                                                                <tr key={line.id} style={{backgroundColor: '#f5f5f5'}}>
                                                                    <td></td>
                                                                    <td>
                                                                        <FontAwesomeIcon 
                                                                            icon={faTrash} 
                                                                            size="1x"
                                                                            style={{
                                                                                width: 28,
                                                                                fontSize: 18,
                                                                                cursor: 'pointer'
                                                                            }}
                                                                            onClick={() => this.removePartBulkLine(line.id)}
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <div data-part-line-id={line.id}>
                                                                            <Autocomplete autoHighlight
                                                                                id={`ddlAddPartBulk_${line.id}`}
                                                                                style={{width: '100%'}}
                                                                                options={this.state.parts ? this.state.parts.filter(x => x.part.isActive && 
                                                                                    !this.state.partFamilyRelationships.find(rel => rel.partId === x.part.id)
                                                                                ).map(x => ({
                                                                                    value: x.part.id,
                                                                                    title: `${x.part.partId} rev ${x.part.revision}`,
                                                                                    part: x.part
                                                                                })) : []}
                                                                                onChange={(event, newValue) => this.handlePartBulkChange(line.id, newValue)}
                                                                                value={line.selectedPart}
                                                                                getOptionLabel={(option) => option.title || ''}
                                                                                isOptionEqualToValue={(option, value) => option.value === value?.value}
                                                                                className="autocomplete-dropdown"
                                                                                onKeyDown={(event) => this.handlePartBulkKeyDown(line.id, event)}
                                                                                renderOption={(option) => <PartAutocompleteOption option={option} classes={this.props.classes} />}
                                                                                renderInput={(params) => <TextField {...params}
                                                                                    autoComplete="new-password" 
                                                                                    variant="outlined"
                                                                                    size="small"
                                                                                    autoFocus={index === this.state.pendingParts.length - 1}
                                                                                />}
                                                                            />
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <TextField
                                                                            type="number"
                                                                            value={line.packageQuantity}
                                                                            onChange={(e) => this.handlePartBulkQuantityChange(line.id, 'packageQuantity', e.target.value)}
                                                                            variant="outlined"
                                                                            size="small"
                                                                            inputProps={{ min: 1 }}
                                                                            style={{width: '70px'}}
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <TextField
                                                                            type="number"
                                                                            value={line.purchaseQuantity}
                                                                            onChange={(e) => this.handlePartBulkQuantityChange(line.id, 'purchaseQuantity', e.target.value)}
                                                                            variant="outlined"
                                                                            size="small"
                                                                            inputProps={{ min: 1 }}
                                                                            style={{width: '70px'}}
                                                                        />
                                                                    </td>
                                                                    <td style={{textAlign: 'center'}}>
                                                                        <Checkbox
                                                                            checked={line.isImpliedPart || false}
                                                                            onChange={(e) => this.handlePartBulkQuantityChange(line.id, 'isImpliedPart', e.target.checked)}
                                                                            size="small"
                                                                            color="primary"
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                            </tbody>
                                                        </table>
                                                    )}
                                                </Droppable>
                                            </DragDropContext>
                                        </div>

                                        <div style={{marginTop: 20, textAlign: 'center'}}>
                                            {!this.state.showPartBulkEdit && this.state.selectedFamily ? (
                                                <Button 
                                                    onClick={() => {
                                                        this.setState({
                                                            showPartBulkEdit: true,
                                                            pendingParts: [{ id: uuid(), selectedPart: null, packageQuantity: 1, purchaseQuantity: 1, isImpliedPart: false, isComplete: false }]
                                                        });
                                                        // Focus the first autocomplete after a brief delay
                                                        setTimeout(() => {
                                                            const autocompleteElement = document.querySelector('[data-part-line-id] input');
                                                            if (autocompleteElement) {
                                                                autocompleteElement.focus();
                                                            }
                                                        }, 100);
                                                    }}
                                                    primary={true}
                                                    className="success-button-lg"
                                                >Add Lines</Button>
                                            ) : this.state.showPartBulkEdit && this.state.selectedFamily ? (
                                                <div style={{display: 'flex', justifyContent: 'center', gap: '10px'}}>
                                                    {this.state.savingNewValue ? (
                                                        <CircularProgress />
                                                    ) : (
                                                        <>
                                                            <Button 
                                                                onClick={this.saveAllPartBulkChanges}
                                                                primary={true}
                                                                className="success-button-lg"
                                                                disabled={this.state.pendingParts.filter(line => line.selectedPart).length === 0}
                                                            >Save All Changes</Button>
                                                            <Button 
                                                                onClick={this.cancelPartBulkEdit}
                                                                className="cancel-button-lg"
                                                            >Cancel</Button>
                                                        </>
                                                    )}
                                                </div>
                                            ) : null}
                                        </div>
                                        
                                        {this.state.hasQuantityChanges && (
                                            <div style={{marginTop: 20, textAlign: 'center'}}>
                                                {this.state.savingNewValue ? (
                                                    <CircularProgress />
                                                ) : (
                                                    <Button 
                                                        onClick={this.saveAllQuantityChanges}
                                                        primary={true}
                                                        className="success-button-lg"
                                                    >Save</Button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {this.state.selectedType === 'Assemblies' && (
                            <div style={{marginTop: 20}}>
                                {this.state.assemblyFamily && (
                                    <div>
                                        <h3>{this.state.assemblyFamilies && this.state.assemblyFamilies.find(x => x.id === this.state.assemblyFamily) ? this.state.assemblyFamilies.find(x => x.id === this.state.assemblyFamily).description : ''}</h3>
                                        
                                        <div className="parts-table-container">
                                            <DragDropContext onDragEnd={this.onDragEnd}>
                                                <Droppable droppableId="assembly-family-table">
                                                    {(provided) => (
                                                        <table className="parts-table no-sticky-headers" ref={provided.innerRef} {...provided.droppableProps}>
                                                            <thead>
                                                            <tr>
                                                                <th style={{width: 50}}>Order</th>
                                                                <th style={{width: 50}}>Remove</th>
                                                                <th style={{minWidth: 120}}>Assembly</th>
                                                            </tr>
                                                            </thead>
                                                            <tbody>
                                                            {this.state.assemblies && this.state.assemblies
                                                                .filter(x => x.assembly.isActive && x.assembly.assemblyFamilyId === this.state.assemblyFamily)
                                                                .sort((a, b) => (a.assembly.assemblyFamilyOrder || 0) - (b.assembly.assemblyFamilyOrder || 0))
                                                                .map((assemblyExpanded, index) => {
                                                                const assembly = assemblyExpanded.assembly;
                                                                if (!assembly || !assembly.isActive) return null;

                                                                return (
                                                                    <Draggable key={assembly.id} draggableId={assembly.id.toString()} index={index}>
                                                                        {(provided) => (
                                                                            <tr ref={provided.innerRef} {...provided.draggableProps} style={this.state.permissions.includes('EditAssembliesAndParts') ? {
                                                                                cursor: 'pointer',
                                                                                opacity: assembly.isActive ? 1 : 0.6,
                                                                                ...provided.draggableProps.style
                                                                            } : {}}>
                                                                                <td {...provided.dragHandleProps}>
                                                                                    <FontAwesomeIcon icon={faGripVertical} />
                                                                                </td>
                                                                                <td>
                                                                                    <FontAwesomeIcon 
                                                                                        icon={faTrash} 
                                                                                        size="1x"
                                                                                        style={{
                                                                                            width: 28,
                                                                                            fontSize: 18,
                                                                                            cursor: 'pointer'
                                                                                        }}
                                                                                        onClick={() => this.removeAssemblyFromFamily(assembly.id)}
                                                                                    />
                                                                                </td>
                                                                                <td className="text-left">
                                                                                    {this.state.permissions.includes('EditAssembliesAndParts') ? (
                                                                                        <div style={{cursor: 'pointer'}} onClick={(event) => this.editAssembly(assemblyExpanded, event.ctrlKey)}>
                                                                                            <PartDisplay 
                                                                                                part={{
                                                                                                    partId: assembly.assemblyId,
                                                                                                    revision: assembly.revision,
                                                                                                    description: assembly.description
                                                                                                }}
                                                                                                variant="table"
                                                                                                classes={this.props.classes}
                                                                                            />
                                                                                        </div>
                                                                                    ) : (
                                                                                        <PartDisplay 
                                                                                            part={{
                                                                                                partId: assembly.assemblyId,
                                                                                                revision: assembly.revision,
                                                                                                description: assembly.description
                                                                                            }}
                                                                                            variant="table"
                                                                                            classes={this.props.classes}
                                                                                        />
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        )}
                                                                    </Draggable>
                                                                );
                                                            })}
                                                            {provided.placeholder}
                                                            {this.state.showAssemblyBulkEdit && this.state.pendingAssemblies.map((line, index) => (
                                                                <tr key={line.id} style={{backgroundColor: '#f5f5f5'}}>
                                                                    <td></td>
                                                                    <td>
                                                                        <FontAwesomeIcon 
                                                                            icon={faTrash} 
                                                                            size="1x"
                                                                            style={{
                                                                                width: 28,
                                                                                fontSize: 18,
                                                                                cursor: 'pointer'
                                                                            }}
                                                                            onClick={() => this.removeAssemblyBulkLine(line.id)}
                                                                        />
                                                                    </td>
                                                                    <td colSpan="3">
                                                                        <div data-line-id={line.id}>
                                                                            <Autocomplete autoHighlight
                                                                                id={`ddlAddAssemblyBulk_${line.id}`}
                                                                                style={{width: '100%'}}
                                                                                options={this.state.assemblies ? this.state.assemblies.filter(x => x.assembly.isActive && 
                                                                                    (!x.assembly.assemblyFamilyId || x.assembly.assemblyFamilyId !== this.state.assemblyFamily)
                                                                                ).map(x => ({
                                                                                    type: 'assembly',
                                                                                    partId: x.assembly.assemblyId,
                                                                                    revision: x.assembly.revision,
                                                                                    description: x.assembly.description,
                                                                                    value: x.assembly.id
                                                                                })) : []}
                                                                                onChange={(event, newValue) => this.handleAssemblyBulkChange(line.id, newValue)}
                                                                                value={line.selectedAssembly}
                                                                                getOptionLabel={(option) => option.partId ? `${option.partId} rev ${option.revision}` : ''}
                                                                                isOptionEqualToValue={(option, value) => option.value === value?.value}
                                                                                className="autocomplete-dropdown"
                                                                                renderOption={(option) => <PartAutocompleteOption option={option} classes={this.props.classes} />}
                                                                                onKeyDown={(event) => this.handleAssemblyBulkKeyDown(line.id, event)}
                                                                                renderInput={(params) => <TextField {...params} 
                                                                                    autoComplete="new-password" 
                                                                                    variant="outlined"
                                                                                    autoFocus={index === this.state.pendingAssemblies.length - 1}
                                                                                />}
                                                                            />
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                            </tbody>
                                                        </table>
                                                    )}
                                                </Droppable>
                                            </DragDropContext>
                                        </div>

                                        <div style={{marginTop: 20, textAlign: 'center'}}>
                                            {!this.state.showAssemblyBulkEdit ? (
                                                <Button 
                                                    onClick={() => {
                                                        this.setState({
                                                            showAssemblyBulkEdit: true,
                                                            pendingAssemblies: [{ id: uuid(), selectedAssembly: null, isComplete: false }]
                                                        });
                                                        // Focus the first autocomplete after a brief delay
                                                        setTimeout(() => {
                                                            const autocompleteElement = document.querySelector('[data-line-id] input');
                                                            if (autocompleteElement) {
                                                                autocompleteElement.focus();
                                                            }
                                                        }, 100);
                                                    }}
                                                    primary={true}
                                                    className="success-button-lg"
                                                >Add Lines</Button>
                                            ) : (
                                                <div style={{display: 'flex', justifyContent: 'center', gap: '10px'}}>
                                                    {this.state.savingNewValue ? (
                                                        <CircularProgress />
                                                    ) : (
                                                        <>
                                                            <Button 
                                                                onClick={this.saveAllAssemblyBulkChanges}
                                                                primary={true}
                                                                className="success-button-lg"
                                                                disabled={this.state.pendingAssemblies.filter(line => line.selectedAssembly).length === 0}
                                                            >Save All Changes</Button>
                                                            <Button 
                                                                onClick={this.cancelAssemblyBulkEdit}
                                                                className="cancel-button-lg"
                                                            >Cancel</Button>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Part Family Edit Drawer */}
                        {this.state.editPartFamilyOpen && this.state.editingPartFamily && (
                            <DrawerMenuRight 
                                title="Edit Family" customWidth={600}
                                open={this.state.editPartFamilyOpen}
                                onClose={() => this.setState({editPartFamilyOpen: false})}
                                hasUnsavedChanges={
                                    this.state.editingPartFamily && (
                                        this.state.editingPartFamily.partFamilyId !== this.state.editingPartFamilyOriginal?.partFamilyId ||
                                        this.state.editingPartFamily.description !== this.state.editingPartFamilyOriginal?.description ||
                                        this.state.editingPartFamily.nickname !== this.state.editingPartFamilyOriginal?.nickname ||
                                        this.state.editingPartFamily.imageURL !== this.state.editingPartFamilyOriginal?.imageURL
                                    )
                                }
                                onSubmit={this.savePartFamily}
                                submitConfirmMessage="Are you sure you want to save this family?"
                            >
                                <TextField
                                    placeholder="Family Name" 
                                    label="Family Name"
                                    variant="outlined"
                                    fullWidth
                                    value={this.state.editingPartFamily.partFamilyId}
                                    onChange={this.editPartFamilyId}
                                    style={{marginBottom: 20}}
                                />
                                <TextField
                                    placeholder="Nickname" 
                                    label="Nickname"
                                    variant="outlined"
                                    fullWidth
                                    value={this.state.editingPartFamily.nickname || ''}
                                    onChange={this.editPartFamilyNickname}
                                    inputProps={{maxLength: 10}}
                                    style={{marginBottom: 20}}
                                />
                                <TextField
                                    placeholder="Description" 
                                    label="Description"
                                    variant="outlined"
                                    fullWidth
                                    value={this.state.editingPartFamily.description}
                                    onChange={this.editPartFamilyDescription}
                                    style={{marginBottom: 20}}
                                />
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    marginBottom: 20
                                }}>
                                    <TextField
                                        placeholder="Image URL" 
                                        label="Image URL"
                                        variant="outlined"
                                        value={this.state.editingPartFamily.imageURL || ''}
                                        onChange={this.editPartFamilyImageURL}
                                        style={{width: '80%'}}
                                    />
                                    {this.state.editingPartFamily.imageURL && this.state.editingPartFamily.imageURL.length > 0 ? (
                                        <img
                                            src={this.state.editingPartFamily.imageURL}
                                            alt={this.state.editingPartFamily.partFamilyId}
                                            width="18%"
                                            style={{display: 'inline-block', marginLeft: 10, maxHeight: '60px', objectFit: 'contain'}}
                                        />
                                    ) : null}
                                </div>
                                <div style={{marginBottom: 20, textAlign: 'center'}}>
                                    <input
                                        type="file"
                                        id="edit-part-family-image-upload"
                                        accept="image/*"
                                        style={{display: 'none'}}
                                        onChange={this.handlePartFamilyImageUpload}
                                    />
                                    <label htmlFor="edit-part-family-image-upload">
                                        <Button 
                                            onClick={() => document.getElementById('edit-part-family-image-upload').click()}
                                            primary={false}
                                            className="success-button-lg"
                                        >Upload Image</Button>
                                    </label>
                                </div>
                                <center>
                                    {this.state.savingNewValue ? (
                                        <CircularProgress />
                                    ) : (
                                        <Button 
                                            onClick={this.savePartFamily}
                                            primary={true}
                                            className="success-button-lg" 
                                            style={{margin: '20px auto 0', display: 'block'}}
                                        >Save</Button>
                                    )}
                                </center>
                            </DrawerMenuRight>
                        )}

                        {/* Part to Family Drawer */}
                        {this.state.partToFamilyOpen && this.state.editingPartFamily && (
                            <DrawerMenuRight 
                                title="Add Part To Family" customWidth={600}
                                open={this.state.partToFamilyOpen}
                                onClose={() => this.setState({partToFamilyOpen: false})}
                                hasUnsavedChanges={
                                    this.state.selectedPartForFamily !== null ||
                                    this.state.newPartToFamilyPackageQuantity !== '' ||
                                    this.state.newPartToFamilyPurchaseQuantity !== '' ||
                                    this.state.newPartToFamilyIsImpliedPart
                                }
                            >
                                <Autocomplete autoHighlight
                                    id="ddlPart"
                                    style={{marginBottom: 20, maxWidth: 400}}
                                    options={this.state.parts ? this.state.parts.filter(x => x.part.isActive).map(x => ({
                                        value: x.part.id,
                                        label: `${x.part.partId} rev ${x.part.revision}`,
                                        part: x.part
                                    })) : []}
                                    onChange={this.handlePartChange}
                                    value={this.state.selectedPartForFamily}
                                    getOptionLabel={(option) => option.label || ''}
                                    isOptionEqualToValue={(option, value) => option.value === value}
                                    className="autocomplete-dropdown"
                                    renderOption={(option) => <PartAutocompleteOption option={option} classes={this.props.classes} />}
                                    renderInput={(params) => <TextField {...params} inputProps={{
                                        ...params.inputProps
                                    }} label="Part" autoComplete="new-password" variant="outlined"/>}
                                />
                                <TextField
                                    placeholder="Package Quantity" 
                                    label="Package Quantity"
                                    variant="outlined"
                                    fullWidth
                                    type="number"
                                    value={this.state.newPartToFamilyPackageQuantity}
                                    onChange={(e) => this.setState({newPartToFamilyPackageQuantity: e.target.value})}
                                    style={{marginBottom: 20}}
                                />
                                <TextField
                                    placeholder="Purchase Quantity" 
                                    label="Purchase Quantity"
                                    variant="outlined"
                                    fullWidth
                                    type="number"
                                    value={this.state.newPartToFamilyPurchaseQuantity}
                                    onChange={(e) => this.setState({newPartToFamilyPurchaseQuantity: e.target.value})}
                                    style={{marginBottom: 20}}
                                />
                                <div style={{display: 'flex', alignItems: 'center', marginBottom: 20}}>
                                    <Checkbox
                                        checked={this.state.newPartToFamilyIsImpliedPart}
                                        onChange={(e) => this.setState({newPartToFamilyIsImpliedPart: e.target.checked})}
                                        color="primary"
                                    />
                                    <span style={{marginLeft: 8}}>
                                        Implied Part (excludes from purchase orders and inventory tracking when ordered as part of family)
                                    </span>
                                </div>
                                {this.state.savingNewValue ? (
                                    <CircularProgress />
                                ) : (
                                    <Button 
                                        onClick={this.addPartToFamily}
                                        primary={true}
                                        className="success-button-lg" 
                                        style={{margin: '20px 0'}}
                                    >Add</Button>
                                )}
                            </DrawerMenuRight>
                        )}



                        {/* Assembly Family Edit Drawer */}
                        {this.state.editAssemblyFamilyOpen && this.state.editingAssemblyFamily && (
                            <DrawerMenuRight 
                                title="Edit Assembly Family" customWidth={600}
                                open={this.state.editAssemblyFamilyOpen}
                                onClose={() => this.setState({editAssemblyFamilyOpen: false})}
                                hasUnsavedChanges={
                                    this.state.editingAssemblyFamily && this.state.editingAssemblyFamilyOriginal && (
                                        this.state.editingAssemblyFamily.assemblyFamilyId !== this.state.editingAssemblyFamilyOriginal.assemblyFamilyId ||
                                        this.state.editingAssemblyFamily.description !== this.state.editingAssemblyFamilyOriginal.description
                                    )
                                }
                            >
                                <TextField
                                    placeholder="Family Name" 
                                    label="Family Name"
                                    variant="outlined"
                                    fullWidth
                                    defaultValue={this.state.editingAssemblyFamily.assemblyFamilyId}
                                    onChange={this.editAssemblyFamilyId}
                                    style={{marginBottom: 20}}
                                />
                                <TextField
                                    placeholder="Description" 
                                    label="Description"
                                    variant="outlined"
                                    fullWidth
                                    defaultValue={this.state.editingAssemblyFamily.description}
                                    onChange={this.editAssemblyFamilyDescription}
                                />
                                {this.state.savingNewValue ? (
                                    <CircularProgress />
                                ) : (
                                    <Button 
                                        onClick={this.saveAssemblyFamily}
                                        primary={true}
                                        className="success-button-lg"
                                        style={{margin: '20px 0'}}
                                    >Save</Button>
                                )}
                            </DrawerMenuRight>
                        )}

                        {/* Assembly to Family Drawer */}
                        {this.state.assemblyToFamilyOpen && this.state.editingAssemblyFamily && (
                            <DrawerMenuRight
                                title={`Add Assembly To ${this.state.editingAssemblyFamily.assemblyFamilyId}`} customWidth={600}
                                open={this.state.assemblyToFamilyOpen}
                                onClose={() => this.setState({assemblyToFamilyOpen: false})}
                                hasUnsavedChanges={this.state.selectedAssemblyForFamily !== ''}
                            >
                                <Autocomplete autoHighlight
                                    id="ddlAssembly"
                                    options={assemblyOptions}
                                    onChange={this.handleAssemblyChange}
                                    getOptionLabel={(option) => option.partId ? `${option.partId} rev ${option.revision}` : ''}
                                    getOptionSelected={(option) => option.value === this.state.selectedAssemblyForFamily}
                                    className="autocomplete-dropdown"
                                    style={{maxWidth: 600, marginBottom: 20}}
                                    renderOption={(option) => <PartAutocompleteOption option={option} classes={this.props.classes} />}
                                    renderInput={(params) => <TextField {...params}
                                                                        inputProps={{
                                                                            ...params.inputProps
                                                                        }} label="Search"
                                                                        autoComplete="new-password"
                                                                        variant="outlined"/>}
                                />
                                {this.state.savingNewValue ? (
                                    <CircularProgress />
                                ) : (
                                    <Button 
                                        onClick={this.addAssemblyToFamily}
                                        primary={true}
                                        className="success-button-lg"
                                        style={{margin: '20px 0'}}
                                    >Add</Button>
                                )}
                            </DrawerMenuRight>
                        )}

                        {/* New Part Family Drawer */}
                        {this.state.newPartFamilyOpen && (
                            <DrawerMenuRight 
                                title="New Part Family" customWidth={600}
                                open={this.state.newPartFamilyOpen}
                                onClose={() => this.setState({newPartFamilyOpen: false})}
                                hasUnsavedChanges={
                                    this.state.newPartFamilyName !== '' ||
                                    this.state.newPartFamilyDescription !== '' ||
                                    this.state.newPartFamilyNickname !== '' ||
                                    this.state.newPartFamilyImageURL !== ''
                                }
                                onSubmit={() => {
                                    this.saveNewPartFamily();
                                    this.setState({newPartFamilyOpen: false});
                                }}
                                submitConfirmMessage="Are you sure you want to create this family?"
                            >
                                <TextField
                                    placeholder="Family Name" 
                                    label="Family Name"
                                    variant="outlined"
                                    fullWidth
                                    value={this.state.newPartFamilyName}
                                    onChange={this.changeNewPartFamilyName}
                                    style={{marginBottom: 20}}
                                    inputProps={{maxLength: 50}}
                                />
                                <TextField
                                    placeholder="Nickname" 
                                    label="Nickname"
                                    variant="outlined"
                                    fullWidth
                                    value={this.state.newPartFamilyNickname}
                                    onChange={this.changeNewPartFamilyNickname}
                                    inputProps={{maxLength: 10}}
                                    style={{marginBottom: 20}}
                                />
                                <TextField
                                    placeholder="Description" 
                                    label="Description"
                                    variant="outlined"
                                    fullWidth
                                    value={this.state.newPartFamilyDescription}
                                    onChange={this.changeNewPartFamilyDescription}
                                    inputProps={{maxLength: 500}}
                                />
                                {this.state.savingNewValue ? (
                                    <CircularProgress />
                                ) : (
                                    <Button 
                                        onClick={() => {
                                            this.saveNewPartFamily();
                                            this.setState({newPartFamilyOpen: false});
                                        }}
                                        primary={true}
                                        className="success-button-lg" 
                                        style={{margin: '20px 0'}}
                                    >Create Family</Button>
                                )}
                            </DrawerMenuRight>
                        )}

                        {/* New Assembly Family Drawer */}
                        {this.state.newAssemblyFamilyOpen && (
                            <DrawerMenuRight 
                                title="New Assembly Family" customWidth={600}
                                open={this.state.newAssemblyFamilyOpen}
                                onClose={() => this.setState({newAssemblyFamilyOpen: false})}
                                hasUnsavedChanges={
                                    this.state.newFamilyName !== '' ||
                                    this.state.newFamilyDescription !== ''
                                }
                                onSubmit={() => {
                                    this.saveNewAssemblyFamily();
                                    this.setState({newAssemblyFamilyOpen: false});
                                }}
                                submitConfirmMessage="Are you sure you want to create this assembly family?"
                            >
                                <TextField
                                    placeholder="Family Name" 
                                    label="Family Name"
                                    variant="outlined"
                                    fullWidth
                                    value={this.state.newFamilyName}
                                    onChange={this.changeNewFamilyName}
                                    style={{marginBottom: 20}}
                                    inputProps={{maxLength: 50}}
                                />
                                <TextField
                                    placeholder="Description" 
                                    label="Description"
                                    variant="outlined"
                                    fullWidth
                                    value={this.state.newFamilyDescription}
                                    onChange={this.changeNewFamilyDescription}
                                    style={{marginBottom: 20}}
                                    inputProps={{maxLength: 500}}
                                />
                                {this.state.savingNewValue ? (
                                    <CircularProgress />
                                ) : (
                                    <Button 
                                        onClick={() => {
                                            this.saveNewAssemblyFamily();
                                            this.setState({newAssemblyFamilyOpen: false});
                                        }}
                                        primary={true}
                                        className="success-button-lg" 
                                        style={{margin: '20px 0'}}
                                    >Create Family</Button>
                                )}
                            </DrawerMenuRight>
                        )}
                    </div>
                </div>
            </DashboardTemplate>
        );
    }
}

AdminFamilies.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(AdminFamilies); 